import type { ErrorComponentProps } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ContentError, EmptyState, Button } from "@core/components";
import { isApiError } from "@core/errors";

/**
 * What the cohort routes show when the cohort cannot be loaded. `404 cohort_not_found` is the
 * normal « not available » state: the cohort does not exist OR is outside this account's scope
 * (the API does not tell which, and neither do we). Anything else is the generic failure panel
 * with its retry. Both appear inside the frame.
 */
export function CohortRouteError(props: ErrorComponentProps) {
  const { t } = useTranslation("cohorts");
  const navigate = useNavigate();

  if (isApiError(props.error) && props.error.status === 404) {
    return (
      <EmptyState
        illustration="cohorts"
        title={t("not-found.title")}
        description={t("not-found.description")}
        className="min-h-75 justify-center"
        action={
          <Button
            variant="secondary"
            onPress={() => {
              void navigate({ to: "/cohorts" });
            }}
          >
            {t("not-found.back")}
          </Button>
        }
      />
    );
  }

  return <ContentError {...props} />;
}
